import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Plus, Trash2, ArrowUp, ArrowDown, Copy, Type, List as ListIcon, Image as ImageIcon, Images, Briefcase, Quote, Minus, X, ChevronDown, ChevronRight, GripVertical, FileText, Star, Building2, Music2, Loader2 } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Field, ImageUpload, AudioUpload, I18nInput } from "./Fields";
import { uploadVideo, VIDEO_TYPES, MAX_VIDEO } from "../../lib/video";
import { toast } from "sonner";
import { nanoid } from "nanoid";

const TYPES = [
  { key: "heading", label: "Başlık (bölüm ayırıcı)", icon: Type, color: "text-amber-700 bg-amber-50" },
  { key: "text", label: "Metin / Paragraf", icon: Type, color: "text-blue-700 bg-blue-50" },
  { key: "description", label: "Uzun açıklama (ana metin)", icon: FileText, color: "text-sky-700 bg-sky-50" },
  { key: "highlights", label: "Öne çıkan başlıklar (sidebar)", icon: Star, color: "text-amber-700 bg-amber-50" },
  { key: "list", label: "Madde listesi", icon: ListIcon, color: "text-emerald-700 bg-emerald-50" },
  { key: "image", label: "Tek görsel", icon: ImageIcon, color: "text-amber-700 bg-amber-50" },
  { key: "gallery", label: "Galeri (çoklu görsel)", icon: Images, color: "text-rose-700 bg-rose-50" },
  { key: "media", label: "Medya (müzik / video)", icon: Music2, color: "text-fuchsia-700 bg-fuchsia-50" },
  { key: "cases", label: "Çalışma kartları grid", icon: Briefcase, color: "text-indigo-700 bg-indigo-50" },
  { key: "references", label: "Referans grid", icon: Quote, color: "text-purple-700 bg-purple-50" },
  { key: "company", label: "Firma bilgi kartı", icon: Building2, color: "text-amber-700 bg-amber-50" },
  { key: "spacer", label: "Boşluk / Ayraç", icon: Minus, color: "text-stone-700 bg-stone-100" },
];

const defaults = {
  heading: () => ({ level: "h2", align: "left" }),
  text: () => ({ html: { tr: "", en: "" } }),
  description: () => ({}),
  highlights: () => ({ layout: "sidebar" }),
  list: () => ({ style: "bullet", items: [{ tr: "", en: "" }] }),
  image: () => ({ src: "", caption: { tr: "", en: "" }, size: "large", align: "center", height: 0 }),
  gallery: () => ({ items: [], cols: 3 }),
  media: () => ({ kind: "audio", source: "url", url: "", file_url: "", cover: "", artist: "", player_style: "card", autoplay: false, description: { tr: "", en: "" }, detail: { tr: "", en: "" } }),
  cases: () => ({ useAll: true, items: [] }),
  references: () => ({ useAll: true, items: [] }),
  company: () => ({ layout: "card", show_map: true }),
  spacer: () => ({ height: 48 }),
};

export function newBlock(type = "text") {
  const defaultSpan = type === "media" ? "quarter" : "full";
  return { id: nanoid(10), type, title: { tr: "", en: "" }, span: defaultSpan, data: defaults[type]() };
}

const SPANS = [
  { key: "full", label: "Tam", hint: "12/12" },
  { key: "two_thirds", label: "2/3", hint: "8/12" },
  { key: "half", label: "1/2", hint: "6/12" },
  { key: "third", label: "1/3", hint: "4/12" },
  { key: "quarter", label: "1/4", hint: "3/12" },
];

function SpanPicker({ value, onChange, testid }) {
  const current = SPANS.find((s) => s.key === value) || SPANS[0];
  return (
    <div className="hidden sm:flex items-center gap-0.5 bg-stone-100 rounded-full p-0.5" title={`Genişlik: ${current.label} (${current.hint})`} data-testid={testid}>
      {SPANS.map((s) => (
        <button
          key={s.key}
          type="button"
          onClick={() => onChange(s.key)}
          className={`text-[11px] font-medium px-2 py-1 rounded-full transition-colors ${value === s.key ? "bg-white shadow text-stone-900" : "text-stone-500 hover:text-stone-800"}`}
          data-testid={`${testid}-${s.key}`}
          aria-pressed={value === s.key}
        >{s.label}</button>
      ))}
    </div>
  );
}

function I18nRow({ value, onChange, placeholder, testid, textarea = false }) {
  const Cmp = textarea ? Textarea : Input;
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      <Cmp value={value?.tr || ""} onChange={(e) => onChange({ ...(value || {}), tr: e.target.value })} placeholder={`${placeholder} (TR)`} data-testid={`${testid}-tr`} rows={textarea ? 4 : undefined} />
      <Cmp value={value?.en || ""} onChange={(e) => onChange({ ...(value || {}), en: e.target.value })} placeholder={`${placeholder} (EN)`} data-testid={`${testid}-en`} rows={textarea ? 4 : undefined} />
    </div>
  );
}

/* ---- Per-type editors ---- */

function HeadingEditor({ data, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Field label="Seviye" hint="H2 büyük, H3 orta, H4 küçük.">
        <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={data.level || "h2"} onChange={(e) => onChange({ ...data, level: e.target.value })} data-testid="block-heading-level">
          <option value="h2">H2 — Büyük başlık</option>
          <option value="h3">H3 — Alt başlık</option>
          <option value="h4">H4 — Küçük başlık</option>
        </select>
      </Field>
      <Field label="Hizalama">
        <div className="flex gap-2">
          {[["left","Sol"],["center","Orta"],["right","Sağ"]].map(([k,lbl]) => (
            <button key={k} type="button" onClick={() => onChange({ ...data, align: k })} className={`flex-1 h-9 rounded-md border text-sm font-medium transition ${(data.align||"left")===k?"bg-stone-900 text-white border-stone-900":"bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`} data-testid={`block-heading-align-${k}`}>{lbl}</button>
          ))}
        </div>
      </Field>
    </div>
  );
}

function TextEditor({ data, onChange }) {
  return (
    <Field label="İçerik (HTML destekli — <p>, <strong>, <em>, <ul>, <li>, <a> vb.)">
      <I18nRow value={data.html} onChange={(v) => onChange({ ...data, html: v })} placeholder="İçerik" testid="block-text" textarea />
    </Field>
  );
}

function ListEditor({ data, onChange }) {
  const items = data.items || [];
  const upd = (i, v) => { const nx = [...items]; nx[i] = v; onChange({ ...data, items: nx }); };
  const add = () => onChange({ ...data, items: [...items, { tr: "", en: "" }] });
  const del = (i) => { const nx = [...items]; nx.splice(i, 1); onChange({ ...data, items: nx }); };
  return (
    <div className="space-y-3">
      <Field label="Liste stili">
        <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={data.style || "bullet"} onChange={(e) => onChange({ ...data, style: e.target.value })} data-testid="block-list-style">
          <option value="bullet">Madde (•)</option>
          <option value="numbered">Numaralı (1, 2, 3)</option>
        </select>
      </Field>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="shrink-0 w-6 h-9 flex items-center justify-center text-xs text-stone-400">#{i + 1}</span>
            <div className="flex-1"><I18nRow value={it} onChange={(v) => upd(i, v)} placeholder="Madde metni" testid={`block-list-item-${i}`} /></div>
            <Button type="button" variant="ghost" size="sm" onClick={() => del(i)}><X size={14} /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="block-list-add"><Plus size={14} className="mr-1" />Madde ekle</Button>
      </div>
    </div>
  );
}

function ImageEditor({ data, onChange, idx }) {
  return (
    <div className="space-y-3">
      <ImageUpload label="Görsel" value={data.src} onChange={(v) => onChange({ ...data, src: v })} testid={`block-image-${idx}`} />
      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="Boyut">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={data.size || "large"} onChange={(e) => onChange({ ...data, size: e.target.value })} data-testid="block-image-size">
            <option value="small">Küçük</option>
            <option value="medium">Orta</option>
            <option value="large">Büyük</option>
            <option value="full">Tam genişlik</option>
          </select>
        </Field>
        <Field label="Hizalama">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={data.align || "center"} onChange={(e) => onChange({ ...data, align: e.target.value })}>
            <option value="left">Sol</option>
            <option value="center">Orta</option>
            <option value="right">Sağ</option>
          </select>
        </Field>
        <Field label="Yükseklik (px, 0 = oranla)">
          <Input type="number" min={0} max={1200} value={data.height || 0} onChange={(e) => onChange({ ...data, height: Number(e.target.value) })} placeholder="0" />
        </Field>
      </div>
      <Field label="Alt yazı (opsiyonel)">
        <I18nRow value={data.caption} onChange={(v) => onChange({ ...data, caption: v })} placeholder="Alt yazı" testid="block-image-caption" />
      </Field>
    </div>
  );
}

function GalleryEditor({ data, onChange }) {
  const items = data.items || [];
  const upd = (i, patch) => { const nx = [...items]; nx[i] = { ...nx[i], ...patch }; onChange({ ...data, items: nx }); };
  const add = () => onChange({ ...data, items: [...items, { src: "", caption: { tr: "", en: "" } }] });
  const del = (i) => { const nx = [...items]; nx.splice(i, 1); onChange({ ...data, items: nx }); };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const nx = [...items];
    [nx[i], nx[j]] = [nx[j], nx[i]];
    onChange({ ...data, items: nx });
  };
  return (
    <div className="space-y-3">
      <Field label="Sütun sayısı">
        <select className="w-full border rounded-md h-9 px-3 text-sm bg-white max-w-xs" value={data.cols || 3} onChange={(e) => onChange({ ...data, cols: Number(e.target.value) })} data-testid="block-gallery-cols">
          <option value={2}>2 sütun</option>
          <option value={3}>3 sütun</option>
          <option value={4}>4 sütun</option>
        </select>
      </Field>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="border rounded-lg p-3 bg-white grid sm:grid-cols-[auto_1fr_auto] gap-3 items-start">
            <div className="flex flex-col gap-0.5">
              <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={12} /></Button>
              <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDown size={12} /></Button>
            </div>
            <div className="space-y-2 min-w-0">
              <ImageUpload label={`Görsel #${i + 1}`} value={it.src} onChange={(v) => upd(i, { src: v })} testid={`block-gallery-item-${i}`} />
              <I18nRow value={it.caption} onChange={(v) => upd(i, { caption: v })} placeholder="Alt yazı" testid={`block-gallery-caption-${i}`} />
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => del(i)}><Trash2 size={14} className="text-red-600" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="block-gallery-add"><Plus size={14} className="mr-1" />Görsel ekle</Button>
      </div>
    </div>
  );
}

function CasesEditor({ data, onChange, parentCases = [] }) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={data.useAll !== false} onChange={(e) => onChange({ ...data, useAll: e.target.checked })} />
        <span>Formun üstündeki "Çalışmalar" listesindeki tüm çalışmaları göster</span>
      </label>
      <p className="text-xs text-stone-500">Çalışma kartları bu bloğun bulunduğu yerde grid olarak çıkar ({(parentCases || []).length} çalışma mevcut). Yukarıdaki checkbox'ı kapatırsan şu an bu bloğa özel seçim desteklenmez — tüm çalışmalar yerine bir alt küme eklemek istiyorsan şimdilik ek liste yerine 2 ayrı hizmet oluşturman önerilir.</p>
    </div>
  );
}

function RefsEditor({ data, onChange, parentRefs = [] }) {
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={data.useAll !== false} onChange={(e) => onChange({ ...data, useAll: e.target.checked })} />
        <span>Formun üstündeki "Referanslar" listesindeki tüm referansları göster</span>
      </label>
      <p className="text-xs text-stone-500">Referans kartları bu bloğun bulunduğu yerde grid olarak çıkar ({(parentRefs || []).length} referans mevcut).</p>
    </div>
  );
}

function SpacerEditor({ data, onChange }) {
  return (
    <Field label={`Yükseklik: ${data.height || 48}px`}>
      <input type="range" min="8" max="240" step="4" value={data.height || 48} onChange={(e) => onChange({ ...data, height: Number(e.target.value) })} className="w-full" />
    </Field>
  );
}

function DescriptionEditor() {
  return (
    <div className="text-xs text-stone-600 bg-sky-50/50 border border-sky-200/60 rounded-lg p-3">
      Bu blok, formun üstündeki <strong>"Uzun açıklama"</strong> alanını detay sayfasında bu konumda gösterir. Eklediğinde üstteki sabit metin ikinci kez görünmez.
    </div>
  );
}

function HighlightsEditor({ data, onChange }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-stone-600 bg-amber-50/50 border border-amber-200/60 rounded-lg p-3">
        Formun üstündeki <strong>"Öne çıkan başlıklar"</strong> listesini bu konumda gösterir. Eklediğinde varsayılan sağ-sidebar görünümü kaybolur.
      </div>
      <Field label="Yerleşim">
        <select className="w-full border rounded-md h-9 px-3 text-sm bg-white max-w-xs" value={data.layout || "sidebar"} onChange={(e) => onChange({ ...data, layout: e.target.value })} data-testid="block-highlights-layout">
          <option value="sidebar">Sağ sidebar (CTA buton ile)</option>
          <option value="inline">Tam genişlik şerit</option>
          <option value="grid">Grid (2–3 kolon)</option>
        </select>
      </Field>
    </div>
  );
}

function CompanyBlockEditor({ data, onChange }) {
  return (
    <div className="space-y-3">
      <div className="text-xs text-stone-600 bg-amber-50/50 border border-amber-200/60 rounded-lg p-3">
        Formun üstündeki <strong>"Firma bilgileri"</strong> kartını bu konumda gösterir. Bilgileri değiştirmek için yukarı çık.
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Yerleşim">
          <select className="w-full border rounded-md h-9 px-3 text-sm bg-white" value={data.layout || "card"} onChange={(e) => onChange({ ...data, layout: e.target.value })} data-testid="block-company-layout">
            <option value="card">Kart (logo + bilgiler + harita)</option>
            <option value="compact">Kompakt bar (logo + ad + telefon)</option>
            <option value="split">Yan yana (bilgiler + harita 2 kolon)</option>
          </select>
        </Field>
        <Field label="Harita göster">
          <label className="flex items-center gap-2 text-sm h-9">
            <input type="checkbox" checked={data.show_map !== false} onChange={(e) => onChange({ ...data, show_map: e.target.checked })} data-testid="block-company-showmap" />
            <span>Google haritalar iframe'ini göster</span>
          </label>
        </Field>
      </div>
    </div>
  );
}

function MediaEditor({ data, onChange, idx }) {
  const kind = data.kind || "audio";
  const source = data.source || "url";
  const videoRef = useRef();
  const [vBusy, setVBusy] = useState(false);
  const [vProg, setVProg] = useState(0);
  const uploadVid = async (file) => {
    if (!file) return;
    if (!VIDEO_TYPES.includes(file.type)) return toast.error("Yalnızca MP4, WebM veya MOV yükleyebilirsiniz");
    if (file.size > MAX_VIDEO) return toast.error("Video 1 GB'den büyük olamaz");
    setVBusy(true); setVProg(0);
    try {
      const r = await uploadVideo(file, (p) => setVProg(p));
      onChange({ ...data, file_url: r.url });
      toast.success("Video yüklendi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Yükleme hatası");
    } finally {
      setVBusy(false);
      if (videoRef.current) videoRef.current.value = "";
    }
  };
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Medya türü">
          <div className="flex gap-2">
            {[["audio", "Müzik"], ["video", "Video"]].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                onClick={() => onChange({ ...data, kind: k })}
                className={`flex-1 h-9 rounded-md border text-sm font-medium transition ${kind === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}
                data-testid={`block-media-${idx}-kind-${k}`}
              >{lbl}</button>
            ))}
          </div>
        </Field>
        <Field label="Kaynak">
          <div className="flex gap-2">
            {[["url", kind === "video" ? "YouTube/Vimeo linki" : "URL yapıştır"], ["upload", "Dosya yükle"]].map(([k, lbl]) => (
              <button
                key={k}
                type="button"
                onClick={() => onChange({ ...data, source: k })}
                className={`flex-1 h-9 rounded-md border text-sm font-medium transition ${source === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-300 hover:bg-stone-50"}`}
                data-testid={`block-media-${idx}-source-${k}`}
              >{lbl}</button>
            ))}
          </div>
        </Field>
      </div>

      {source === "url" && (
        <Field label={kind === "video" ? "Video linki (YouTube, Vimeo, Dailymotion veya doğrudan .mp4)" : "Ses linki (SoundCloud, Spotify embed veya doğrudan .mp3)"}>
          <Input
            value={data.url || ""}
            onChange={(e) => onChange({ ...data, url: e.target.value })}
            placeholder={kind === "video" ? "https://www.youtube.com/watch?v=..." : "https://.../sarki.mp3"}
            data-testid={`block-media-${idx}-url`}
          />
        </Field>
      )}

      {source === "upload" && kind === "audio" && (
        <AudioUpload label="Ses dosyası" value={data.file_url} onChange={(v) => onChange({ ...data, file_url: v })} testid={`block-media-${idx}-audio`} />
      )}

      {source === "upload" && kind === "video" && (
        <Field label="Video dosyası" hint="MP4 / WebM / MOV — en fazla 1 GB.">
          <div className="flex items-center gap-3">
            <input ref={videoRef} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => uploadVid(e.target.files[0])} data-testid={`block-media-${idx}-video-input`} />
            <Button type="button" variant="outline" size="sm" disabled={vBusy} onClick={() => videoRef.current.click()} data-testid={`block-media-${idx}-video-button`}>
              {vBusy ? <Loader2 size={14} className="animate-spin mr-1" /> : <ImageIcon size={14} className="mr-1" />}
              {vBusy ? `Yükleniyor ${Math.round(vProg * 100)}%` : "Video yükle"}
            </Button>
            {data.file_url && !vBusy && (
              <>
                <video src={data.file_url?.startsWith("/api") ? data.file_url : data.file_url} controls className="h-14 flex-1 min-w-0 rounded border bg-black" />
                <Button type="button" variant="ghost" size="sm" onClick={() => onChange({ ...data, file_url: "" })}><X size={14} /></Button>
              </>
            )}
          </div>
        </Field>
      )}

      {kind === "audio" && (
        <>
          <div className="grid sm:grid-cols-[1fr_1fr] gap-3">
            <Field label="Sanatçı / Yazar (opsiyonel)">
              <Input
                value={data.artist || ""}
                onChange={(e) => onChange({ ...data, artist: e.target.value })}
                placeholder="Örn. Olgu Tansuğ"
                data-testid={`block-media-${idx}-artist`}
              />
            </Field>
            <Field label="Player stili">
              <select
                className="w-full border rounded-md h-9 px-3 text-sm bg-white"
                value={data.player_style || "card"}
                onChange={(e) => onChange({ ...data, player_style: e.target.value })}
                data-testid={`block-media-${idx}-style`}
              >
                <option value="card">Sade kart (kapak + play + ilerleme)</option>
                <option value="vinyl">Vinyl / dönen plak (süslü, görsel ağırlıklı)</option>
                <option value="strip">Minimal şerit (tek satır)</option>
              </select>
            </Field>
          </div>
          <ImageUpload label="Kapak görseli (opsiyonel)" value={data.cover} onChange={(v) => onChange({ ...data, cover: v })} testid={`block-media-${idx}-cover`} />
        </>
      )}

      <I18nInput label="Küçük açıklama (altında görünür)" value={data.description} onChange={(v) => onChange({ ...data, description: v })} multiline rows={2} testid={`block-media-${idx}-desc`} maxLength={240} />

      <I18nInput label='Detay (gizli, "Detay" butonuna basılınca açılır)' value={data.detail} onChange={(v) => onChange({ ...data, detail: v })} multiline rows={5} testid={`block-media-${idx}-detail`} maxLength={2400} hint="Boş bırakırsan Detay butonu gösterilmez." />
    </div>
  );
}

const EDITORS = {
  heading: HeadingEditor,
  text: TextEditor,
  description: DescriptionEditor,
  highlights: HighlightsEditor,
  list: ListEditor,
  image: ImageEditor,
  gallery: GalleryEditor,
  media: MediaEditor,
  cases: CasesEditor,
  references: RefsEditor,
  company: CompanyBlockEditor,
  spacer: SpacerEditor,
};

function TypePicker({ onPick, label = "Blok ekle" }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    // Position below the button; if there's not enough room, flip above.
    const menuH = Math.min(TYPES.length * 44 + 24, 420);
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow < menuH + 20 ? r.top - menuH - 6 : r.bottom + 6;
    setCoords({ top: Math.max(8, top), left: r.left });
  }, [open]);
  return (
    <div className="relative inline-block">
      <Button ref={btnRef} type="button" variant="outline" size="sm" onClick={() => setOpen((v) => !v)} data-testid="block-add-trigger">
        <Plus size={14} className="mr-1" />{label}
      </Button>
      {open && typeof document !== "undefined" && createPortal(
        <>
          <button type="button" className="fixed inset-0 z-[1000] cursor-default" onClick={() => setOpen(false)} aria-label="kapat" />
          <div
            className="fixed z-[1001] bg-white border rounded-lg shadow-xl p-2 w-72 max-h-[70vh] overflow-auto"
            style={{ top: coords.top, left: coords.left }}
            data-testid="block-type-menu"
          >
            {TYPES.map((T) => {
              const Icon = T.icon;
              return (
                <button key={T.key} type="button" onClick={() => { onPick(T.key); setOpen(false); }} className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-stone-50 text-left" data-testid={`block-add-${T.key}`}>
                  <span className={`w-8 h-8 rounded-md flex items-center justify-center ${T.color}`}><Icon size={14} /></span>
                  <span className="text-sm">{T.label}</span>
                </button>
              );
            })}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}

function Block({ block, idx, total, onChange, onMove, onDelete, onDuplicate, parentCases, parentRefs, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const T = TYPES.find((x) => x.key === block.type) || TYPES[0];
  const Icon = T.icon;
  const Editor = EDITORS[block.type] || (() => null);
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={`border rounded-xl bg-stone-50 overflow-hidden ${isDragging ? "shadow-xl z-10 relative ring-2 ring-amber-400" : ""}`} data-testid={`block-${idx}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-white border-b hover:bg-stone-50 text-left"
        data-testid={`block-${idx}-toggle`}
      >
        <span
          role="button"
          tabIndex={-1}
          className="p-1 text-stone-500 hover:text-stone-900 cursor-grab active:cursor-grabbing touch-none"
          onClick={(e) => e.stopPropagation()}
          {...attributes}
          {...listeners}
          aria-label="Sürükle"
          data-testid={`block-${idx}-drag`}
          title="Sürükle (sırala)"
        >
          <GripVertical size={16} />
        </span>
        <span className="p-1 text-stone-500">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <span className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${T.color}`}><Icon size={13} /></span>
        <span className="flex-1 min-w-0">
          <span className="text-sm font-medium truncate block">
            {block.title?.tr || T.label}
            <span className="text-xs text-stone-400 ml-2">#{idx + 1}</span>
          </span>
        </span>
        <span onClick={(e) => e.stopPropagation()} className="flex items-center gap-1">
          <SpanPicker value={block.span || "full"} onChange={(v) => onChange({ ...block, span: v })} testid={`block-${idx}-span`} />
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onMove(-1)} disabled={idx === 0}><ArrowUp size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onMove(1)} disabled={idx === total - 1}><ArrowDown size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDuplicate} title="Çoğalt"><Copy size={13} /></Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDelete} title="Sil"><Trash2 size={13} className="text-red-600" /></Button>
        </span>
      </button>
      {open && (
        <div className="p-4 space-y-4">
          {block.type !== "spacer" && block.type !== "description" && block.type !== "highlights" && block.type !== "company" && (
            <Field label="Blok başlığı (opsiyonel, boş bırakılırsa görünmez)">
              <I18nRow value={block.title} onChange={(v) => onChange({ ...block, title: v })} placeholder="Başlık" testid={`block-${idx}-title`} />
            </Field>
          )}
          <Editor data={block.data || {}} onChange={(d) => onChange({ ...block, data: d })} idx={idx} parentCases={parentCases} parentRefs={parentRefs} />
        </div>
      )}
    </div>
  );
}

export default function BlockBuilder({ value, onChange, parentCases = [], parentRefs = [] }) {
  const blocks = (Array.isArray(value) ? value : []).map((b) => (b.id ? b : { ...b, id: nanoid(10) }));
  const [justAddedId, setJustAddedId] = useState(null);
  const upd = (i, b) => { const nx = [...blocks]; nx[i] = b; onChange(nx); };
  const add = (type) => {
    const nb = newBlock(type);
    setJustAddedId(nb.id);
    onChange([...blocks, nb]);
  };
  const del = (i) => { const nx = [...blocks]; nx.splice(i, 1); onChange(nx); };
  const dup = (i) => {
    const nb = { ...blocks[i], id: nanoid(10) };
    setJustAddedId(nb.id);
    const nx = [...blocks]; nx.splice(i + 1, 0, nb); onChange(nx);
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const nx = [...blocks];
    [nx[i], nx[j]] = [nx[j], nx[i]];
    onChange(nx);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = blocks.findIndex((b) => b.id === active.id);
    const newIdx = blocks.findIndex((b) => b.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    onChange(arrayMove(blocks, oldIdx, newIdx));
  };

  return (
    <div className="space-y-3">
      {blocks.length === 0 && (
        <div className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center bg-white">
          <p className="text-sm text-stone-500 mb-3">Henüz blok eklemedin. Sayfanın sıralamasını istediğin gibi kurmak için bir blok ekle.</p>
          <TypePicker onPick={add} label="İlk bloğu ekle" />
        </div>
      )}
      {blocks.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {blocks.map((b, i) => (
                <Block
                  key={b.id}
                  block={b}
                  idx={i}
                  total={blocks.length}
                  onChange={(nb) => upd(i, nb)}
                  onMove={(dir) => move(i, dir)}
                  onDelete={() => del(i)}
                  onDuplicate={() => dup(i)}
                  parentCases={parentCases}
                  parentRefs={parentRefs}
                  defaultOpen={b.id === justAddedId}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {blocks.length > 0 && (
        <div className="pt-2"><TypePicker onPick={add} /></div>
      )}
    </div>
  );
}
